import { UserIcon } from '@heroicons/react/24/outline';
import axios from 'axios';
import { useEffect, useState } from 'react';

import { Button } from '@/components/core/Button';
import {
    Dialog,
    DialogBody,
    DialogContent,
    DialogFooter,
    DialogHeader,
    DialogHeaderDefault,
} from '@/components/core/Dialog';
import { Icon } from '@/components/core/Icon';
import { Textarea } from '@/components/core/Textarea';
import { useToast } from '@/hooks/useToast';
import { useTranslation } from '@/hooks/useTranslation';
import { restUrl } from '@/services/utils';

import { contactErrorMessage } from './businessPartner';
import { useInviteOrganization } from './organizationSearch.queries';
import { SearchedOrganization } from './searchedOrganization';

interface SendOrganizationInvitationDialogProps {
    organization?: SearchedOrganization;
    open: boolean;
    onClose: () => void;
}

const SendOrganizationInvitationDialog = ({
    organization,
    open,
    onClose,
}: SendOrganizationInvitationDialogProps) => {
    const { t } = useTranslation();
    const { toast } = useToast();
    const invite = useInviteOrganization();
    const [text, setText] = useState('');

    useEffect(() => {
        if (open) {
            setText('');
        }
    }, [open]);

    const send = () => {
        if (organization == null || invite.isPending) {
            return;
        }
        invite.mutate({
            organizationId: organization.id,
            invitationType: 'CONTACT_REQUEST',
            text,
        }, {
            onSuccess: () => {
                toast.success('INVITATION_SUCCESSFULLY_SENT');
                onClose();
            },
            onError: (error) => {
                const data = axios.isAxiosError(error) ? error.response?.data : undefined;
                toast.error(contactErrorMessage(data, t, 'SAVE_ERROR_MESSAGE') ?? t('SAVE_ERROR_MESSAGE'));
            },
        });
    };

    return (
        <Dialog open={open} onOpenChange={(next) => { if (!next) onClose(); }}>
            <DialogContent variant="large">
                <DialogHeader>
                    <DialogHeaderDefault closeLabel={t('CLOSE')}>
                        {t('INVITATION.TITLE_INVITATION')}
                    </DialogHeaderDefault>
                </DialogHeader>
                <DialogBody className="tw-flex tw-flex-col tw-gap-4">
                    {organization && (
                        <div className="tw-flex tw-items-center tw-gap-4">
                            <div className="tw-flex tw-h-10 tw-w-10 tw-shrink-0 tw-items-center tw-justify-center tw-overflow-hidden tw-rounded-full tw-bg-fill-secondary">
                                {organization.hasImage ? (
                                    <img
                                        src={restUrl(`organizations/${organization.id}/image`)}
                                        alt={organization.name ?? ''}
                                        className="tw-h-full tw-w-full tw-object-cover"
                                    />
                                ) : (
                                    <Icon icon={UserIcon} className="tw-h-5 tw-w-5" variant="secondary" />
                                )}
                            </div>
                            <div className="tw-min-w-0">
                                <div className="tw-break-words tw-text-t-base tw-font-bold tw-text-t-primary lg:tw-text-t-lg">
                                    {organization.name}
                                </div>
                                {organization.gln && (
                                    <div className="tw-text-t-sm tw-text-t-secondary lg:tw-text-t-base">{organization.gln}</div>
                                )}
                            </div>
                        </div>
                    )}
                    <label className="tw-flex tw-flex-col tw-gap-1" htmlFor="invitation-reason-input">
                        <span className="tw-text-t-sm tw-text-t-secondary lg:tw-text-t-base">{t('REASON_FOR_INVITE')}</span>
                        <span className="tw-text-t-sm tw-text-t-secondary lg:tw-text-t-base">{t('Message')}</span>
                        <Textarea
                            id="invitation-reason-input"
                            rows={6}
                            value={text}
                            autoFocus
                            onChange={(event) => setText(event.target.value)}
                        />
                    </label>
                </DialogBody>
                <DialogFooter>
                    <Button
                        id="invite-button"
                        type="button"
                        loading={invite.isPending}
                        onClick={send}
                    >
                        {t('SEND')}
                    </Button>
                    <Button type="button" variant="secondary" onClick={onClose} disabled={invite.isPending}>
                        {t('CLOSE')}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
};

export { SendOrganizationInvitationDialog };
